/**
 * IHS-2 Previous meetings — three columns: home / draw / away.
 * Each column centred with crest (or neutral draw placeholder) on top,
 * big number, and a label below ("Won" or "Draw").
 *
 * Window key (`last-5`, `all-time`, etc.) drives the subtitle copy via
 * i18n so the same engine payload reads natively per market.
 */

import type { ReactElement } from 'react';
import { useTranslation } from '../../../i18n/index.js';
import type {
  IhsPreviousMeetingsData,
  IhsPreviousMeetingsWindow,
} from '../../../types/ihs.js';
import { IhsCard } from './IhsCard.js';
import { TeamCrest, useTeamVisualMode } from '../../primitives/TeamCrest.js';
import styles from './IhsPreviousMeetings.module.css';

function windowLabelKey(w: IhsPreviousMeetingsWindow): string {
  switch (w) {
    case 'last-5':
      return 'ihs.previousMeetings.windowLast5';
    case 'last-10':
      return 'ihs.previousMeetings.windowLast10';
    case 'last-25':
      return 'ihs.previousMeetings.windowLast25';
    case 'all-time':
      return 'ihs.previousMeetings.windowAllTime';
  }
}

function windowLabelDefault(w: IhsPreviousMeetingsWindow): string {
  switch (w) {
    case 'last-5':
      return 'Last 5';
    case 'last-10':
      return 'Last 10';
    case 'last-25':
      return 'Last 25';
    case 'all-time':
      return 'All time';
  }
}

export function IhsPreviousMeetings({
  data,
}: {
  data: IhsPreviousMeetingsData;
}): ReactElement {
  const { t } = useTranslation();
  const total = data.totalMeetings || 0;

  const title =
    data.header?.title ??
    t('ihs.previousMeetings.title', { defaultValue: 'Previous meetings' });
  const subtitle =
    data.header?.subtitle ??
    t(windowLabelKey(data.window), {
      defaultValue: windowLabelDefault(data.window),
    });

  const wonLabel = t('ihs.previousMeetings.wins', { defaultValue: 'Won' });
  const drawLabel = t('ihs.previousMeetings.draws', { defaultValue: 'Draw' });
  const meetingsWord = t('ihs.previousMeetings.meetingsWord', { defaultValue: 'meetings' });
  const teamVisual = useTeamVisualMode();
  void wonLabel;

  return (
    <IhsCard title={title} subtitle={subtitle}>
      {/* Stat Row v2 one-line H2H triple (UX compaction 2026-07-31,
          market research rule 6: H2H is always the W-D-W triple).
          Home wins LEFT, away wins RIGHT (wireframe axis), the centre
          label carries draws + sample size. A tri-segment share bar
          (home / neutral draws / away) sits under the line. Replaces
          the three ~230px columns (~40px now). Team marks flank the
          values per the brand's team-visual mode. */}
      <div className={styles.tripleRow}>
        <span className={styles.sideCell}>
          {teamVisual !== 'none' ? (
            <TeamCrest
              {...(data.home.badge !== undefined ? { src: data.home.badge } : {})}
              name={data.home.name}
              size={16}
            />
          ) : null}
          <span className={styles.count}>{data.homeWins}</span>
        </span>
        <span className={styles.centerLabel} aria-label={`${drawLabel}: ${data.draws}`}>
          {t('ihs.previousMeetings.tripleCenter', {
            draws: data.draws,
            count: total,
            defaultValue: `${data.draws} ${drawLabel.toLowerCase()} — ${total} ${meetingsWord}`,
          })}
        </span>
        <span className={`${styles.sideCell} ${styles.sideCellAway}`}>
          <span className={styles.count}>{data.awayWins}</span>
          {teamVisual !== 'none' ? (
            <TeamCrest
              {...(data.away.badge !== undefined ? { src: data.away.badge } : {})}
              name={data.away.name}
              size={16}
            />
          ) : null}
        </span>
      </div>
      {total > 0 ? (
        <div className={styles.tripleBar} aria-hidden="true">
          <span
            className={styles.barHome}
            style={{ width: `${(data.homeWins / total) * 100}%` }}
          />
          <span
            className={styles.barDraw}
            style={{ width: `${(data.draws / total) * 100}%` }}
          />
          <span
            className={styles.barAway}
            style={{ width: `${(data.awayWins / total) * 100}%` }}
          />
        </div>
      ) : null}
    </IhsCard>
  );
}
